import { Buffer } from "buffer"
import { Borders, Image, Row, Workbook, Worksheet } from "exceljs"
import JsBarcode from "jsbarcode"
import moment from "moment"
import { FormType } from "../../types"

type ExportExcel = {
  data: FormType[]
  companyName?: string
}

export const exportExcel = async ({ data }: ExportExcel) => {
  const { REACT_APP_BASE_URL_API } = process.env
  const prefixImg = REACT_APP_BASE_URL_API + "/getimage/?imgpath="
  const workbook = new Workbook()
  const worksheet = workbook.addWorksheet("Sheet 1")
  const columns = [
    "NO",
    "OPENING DATE",
    "BARCODE",
    "ITEM NAME",
    "PICTURES",
    "STOCK",
    "UNIT",
    "AVERAGE PRICE UNIT",
    "LOCATION"
  ]

  worksheet.views = [{ showGridLines: false }]

  worksheet.mergeCells(2, 1, 1, columns.length)

  // Title
  makeTitle(worksheet)

  // Header
  const headers = worksheet.addRow(columns)
  headers.font = { bold: true }
  headers.eachCell((cell) => {
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF8C308" }
    }
    cell.border = {
      top: { style: "thin", color: { argb: "000000" } },
      left: { style: "thin", color: { argb: "000000" } },
      bottom: { style: "thin", color: { argb: "000000" } },
      right: { style: "thin", color: { argb: "000000" } }
    }
  })

  const barcodeImages = data?.map((item) => {
    const canvas = document.createElement("canvas")
    JsBarcode(canvas, item.product_barcode, { format: "CODE39" })
    const base64 = canvas.toDataURL("image/png")

    return workbook.addImage({
      base64,
      extension: "png"
    })
  })

  const dataImages = await Promise.all(
    data?.map(async (item) => {
      if (item.picture) {
        return Promise.all(
          item.picture.map(async (p) => {
            const imagepath = prefixImg + p.path;
            const response = await fetch(imagepath);
            const arrBuff = await response.arrayBuffer();
            const buffer = Buffer.from(arrBuff);

            return workbook.addImage({ buffer: buffer, extension: "jpeg" } as unknown as Image);
          })
        );
      }
      return [];
    })
  );

  makeRows(data, worksheet, barcodeImages, dataImages)

  // create xlsx
  await workbook.xlsx.writeBuffer().then((buffer) => {
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = "data-entry-opening-stock.xlsx"
    a.click()
    URL.revokeObjectURL(url)
  })
}

function makeTitle(worksheet: Worksheet) {
  const titleCell = worksheet.getCell(1, 2)
  titleCell.value = "DATA ENTRY | OPENING STOCK"
  titleCell.alignment = {
    vertical: "middle",
    horizontal: "center",
    wrapText: true
  }
  titleCell.font = { bold: true, size: 15 }

  worksheet.getColumn(2).width = 18
}

function makeRows(
  data: ExportExcel["data"],
  worksheet: Worksheet,
  barcodeImages: number[],
  dataImages: number[][]
) {

  worksheet.getColumn(3).width = 15
  worksheet.getColumn(5).width = 15

  let itemRow: Row

  const cellBorder: Partial<Borders> = {
    top: { style: "thin", color: { argb: "000000" } },
    left: { style: "thin", color: { argb: "000000" } },
    bottom: { style: "thin", color: { argb: "000000" } },
    right: { style: "thin", color: { argb: "000000" } }
  }

  data.forEach((item, index) => {
    itemRow = worksheet.addRow([
      index + 1,
      moment(item.transaction_date).format("DD/MM/YYYY"),
      "",
      item.product_name,
      "",
      item.soh,
      item.unit_name,
      item.av_price_unit,
      item.location_name
    ])

    itemRow.height = 50

    itemRow.eachCell((cell) => {
      cell.alignment = { vertical: "top" }
      cell.border = cellBorder
    })
  })

  dataImages?.forEach((image, index) => {
    if (image?.length) {
      return worksheet.addImage(image[0], {
        tl: { col: 4, row: index + 3 },
        ext: { width: 100, height: 60 }
      })
    }
  })

  barcodeImages?.forEach((image: any, index: number) => {
    worksheet.addImage(image, {
      tl: { col: 2, row: index + 3 },
      ext: { width: 100, height: 60 }
    })
  })
}
